import { createContext, type ReactNode, useContext, useMemo } from 'react';

import { useQuery } from '@tanstack/react-query';

import { getAuthQueryOptions } from './services';
import type { AuthUser } from './types';

type AuthContextValue = {
  user: AuthUser | null;
  isAuthorized: boolean;
  isLoading: boolean;
};

const AuthContext = createContext<AuthContextValue | null>(null);

export const AuthProvider = ({ children }: { children: ReactNode }) => {
  const { data, isLoading } = useQuery(getAuthQueryOptions());

  const value = useMemo<AuthContextValue>(
    () => ({
      user: data?.user ?? null,
      isAuthorized: Boolean(data?.user),
      isLoading,
    }),
    [data?.user, isLoading],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuthContext = () => {
  const context = useContext(AuthContext);

  if (!context) {
    throw new Error('useAuthContext must be used within AuthProvider');
  }

  return context;
};
